/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import axios from 'axios';
import toastService from '../utils/toastService';
import { ACCOUNT_LOGIN_PATH_PREFIX, EXTERNAL_AUTH_PATHS, getLoginPath } from '../constants/loginRoutes';
import { SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

// Named constants 
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;
const HTTP_STATUS_UNAUTHORIZED = 401;

const apiClient = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api`,
  // timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Restore token from localStorage on app start
const savedToken = localStorage.getItem('auth_access_token');
if (savedToken) {
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

let isRefreshing = false;
let pendingRequests = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle sub-account context required — open the account switcher
    if (error.response?.data?.code === 'SUB_ACCOUNT_REQUIRED') {
      toastService.info(`Select ${SUB_ACCOUNT_LABEL.withArticle} to perform this action.`);
      window.dispatchEvent(new CustomEvent('sub-account-required'));
      return Promise.reject(error);
    }

    // Handle 500 Internal Server Error - Redirect to error page
    if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
      // window.location.href = `/error?type=default&from=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return Promise.reject(error);
    }

    const isAuthCall = originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/refresh') ||
      originalRequest?.url?.includes('/auth/qr/login') ||
      originalRequest?.url?.includes(ACCOUNT_LOGIN_PATH_PREFIX) ||
      EXTERNAL_AUTH_PATHS.some((path) => originalRequest?.url?.includes(path));

    if (error.response?.status === HTTP_STATUS_UNAUTHORIZED && !isAuthCall && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('auth_refresh_token');

      if (!refreshToken) {
        localStorage.removeItem('auth_access_token');
        localStorage.removeItem('auth_user');
        window.location.href = getLoginPath();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await apiClient.post('/auth/refresh', { refreshToken }, {
          headers: {
            Authorization: undefined
          }
        });
        const newToken = data.accessToken;

        localStorage.setItem('auth_access_token', newToken);
        if (data.refreshToken) { localStorage.setItem('auth_refresh_token', data.refreshToken); }
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        pendingRequests.forEach(({ resolve }) => resolve(newToken));
        pendingRequests = [];

        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch {
        pendingRequests.forEach(({ reject }) => reject(error));
        pendingRequests = [];
        localStorage.removeItem('auth_access_token');
        localStorage.removeItem('auth_refresh_token');
        localStorage.removeItem('auth_user');
        delete apiClient.defaults.headers.common['Authorization'];
        window.location.href = getLoginPath();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
